import ky from 'ky';

import { env } from '@/env';
import { useAWAStore } from '@/stores/awa.store';
import { isTokenValid } from '@/utils/token';

export abstract class AWAGlobalApiService {
  static client = ky.create({
    prefixUrl: env.VITE_GLOBAL_API_URL || 'http://localhost:3001',
    headers: {
      // 'Content-Type': 'application/json',
      Accept: 'application/json, text/plain, */*',
    },
    timeout: 20000,
    retry: 0,
    hooks: {
      beforeRequest: [
        async (request) => {
          const { authTokens, setAuthTokens } = useAWAStore.getState();

          // Skip token refresh for the refresh token endpoint itself
          if (request.url.includes('auth/jwt/refresh')) {
            if (authTokens?.accessToken) {
              request.headers.set('Authorization', `${authTokens.accessToken}`);
            }
            return;
          }

          if (authTokens?.accessToken) {
            const valid = isTokenValid(authTokens);

            request.headers.set('authtype', 'fusionauth');

            if (!valid) {
              try {
                const result = await AWAGlobalApiService.refreshAccessToken({
                  accessToken: authTokens.accessToken,
                  refreshToken: authTokens.refreshToken,
                });

                setAuthTokens({
                  accessToken: result.accessToken,
                  accessTokenExpiration: result.accessTokenExpiration ? new Date(result.accessTokenExpiration).getTime() : undefined,
                  refreshToken: result.refreshToken,
                  refreshTokenId: result.refreshTokenId,
                });

                request.headers.set('Authorization', `${result.accessToken}`);
              } catch (error) {
                // If refresh fails, clear the tokens and let the request fail
                setAuthTokens(null);
                throw error;
              }
            } else {
              request.headers.set('Authorization', `${authTokens.accessToken}`);
            }
          }
        },
      ],
    },
  });

  static async getOTP(params: { phoneNumber: string; deliveryMethod?: 'sms' | 'call'; recaptchaToken?: string; isFallback?: boolean }) {
    const res = await this.client.post<{ status: 'invalid-captcha' } | { status: 'valid-captcha'; twoFactorId: string }>(`auth/login/sms`, { json: params });
    return await res.json();
  }

  static async verifyOTP(params: { code: string; twoFactorId: string }) {
    const res = await this.client.post<{
      accessToken: string;
      accessTokenExpiration?: number;
      refreshToken: string;
      refreshTokenId: string;
      trustToken?: string;
      userId?: string;
      username?: string;
    }>(`auth/login/sms-complete`, {
      json: params,
    });
    return await res.json();
  }

  static async getUser() {
    const res = await this.client.get<{
      avatarUrl?: string;
      companyName: string;
      createdAt: string;
      firstName: string;
      fullName?: string;
      id: string;
      lastName: string;
      phoneNumber: string;
      qrData?: string;
      shortUsername?: string;
      signatureUrl?: string;
    }>(`user`);
    return await res.json();
  }

  static async updateUser(params: {
    avatarUrl?: string;
    companyName: string;
    firstName: string;
    fullName?: string;
    lastName: string;
    shortUsername?: string;
    signatureUrl?: string;
  }) {
    const res = await this.client.post<{
      avatarUrl?: string;
      companyName: string;
      createdAt: string;
      firstName: string;
      fullName?: string;
      _id: string;
      lastName: string;
      phoneNumber: string;
      qrData?: string;
      shortUsername?: string;
      signatureUrl?: string;
    }>(`user`, { json: params });
    return await res.json();
  }

  static async uploadData({ data, fileName, type }: { type: 'signature' | 'avatar'; data: string; fileName: string }) {
    const formData = new FormData();
    formData.append('type', type);

    // Convert base64 data URL to Blob
    const response = await fetch(data);
    const blob = await response.blob();
    formData.append(`${type}-file`, blob, fileName);

    const res = await this.client.post<{ url: string }>(`user/upload`, {
      body: formData,
      timeout: 120_000,
    });
    return await res.json();
  }

  static async revokeRefreshToken(refreshToken: string) {
    const res = await this.client.post(`auth/jwt/revoke`, { json: { refreshToken } });
    return await res.json();
  }

  static async refreshAccessToken(params: { accessToken: string; refreshToken: string }) {
    const res = await this.client.post<{ accessToken: string; accessTokenExpiration?: string; refreshToken: string; refreshTokenId: string }>(
      `auth/jwt/refresh`,
      { json: params },
    );
    return await res.json();
  }
}
